/**
 * Enfin Eligible Products modal (React dashboard).
 * Mirrors Angular `EligibleProductEnfinComponent` with escalator filter + GetEnfinPricing refresh.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table as AntTable, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import type { EnfinEligibleProductModalProps } from './EnfinEligibleProductModal.types';
import {
    fetchEnfinEligiblePricingRows,
    fetchEnfinEscalatorOptions,
    getEnfinSolarFinanceMethod,
    getEnfinSvaC4,
    resolveDefaultEnfinEscalator,
    type EnfinEscalatorOption,
} from './enfinEligibleProductUtils';

function fmtUsdCell(n: unknown): string {
    if (n == null || n === '') return '—';
    const x = Number(n);
    if (Number.isNaN(x)) return String(n);
    return `$${x.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

const ENFIN_ELIGIBLE_COLUMNS: ColumnsType<Record<string, unknown>> = [
    {
        title: 'Total KWH Rate',
        dataIndex: 'totalkwhrate',
        key: 'totalkwhrate',
        width: 160,
        render: (v) => fmtUsdCell(v),
        sorter: (a, b) => (Number(a.totalkwhrate) || 0) - (Number(b.totalkwhrate) || 0),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Monthly Payment',
        dataIndex: 'monthlyPayment',
        key: 'monthlyPayment',
        width: 160,
        render: (v) => fmtUsdCell(v),
        sorter: (a, b) => (Number(a.monthlyPayment) || 0) - (Number(b.monthlyPayment) || 0),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'PPW Rate',
        dataIndex: 'epcPaymentPerWatt',
        key: 'epcPaymentPerWatt',
        width: 140,
        render: (v) => fmtUsdCell(v),
        sorter: (a, b) => (Number(a.epcPaymentPerWatt) || 0) - (Number(b.epcPaymentPerWatt) || 0),
        sortDirections: ['ascend', 'descend'],
    },
];

export const EnfinEligibleProductModal: React.FC<EnfinEligibleProductModalProps> = ({
    open,
    onClose,
    theme,
    opportunityId,
    priceQuote,
    opportunityDetails,
    productType,
    fetchBatteryPricing,
    zIndex = 10070,
}) => {
    const [escalatorOptions, setEscalatorOptions] = useState<EnfinEscalatorOption[]>([]);
    const [selectedEscalator, setSelectedEscalator] = useState<string | undefined>(undefined);
    const [solarRows, setSolarRows] = useState<Record<string, unknown>[]>([]);
    const [batteryRows, setBatteryRows] = useState<Record<string, unknown>[]>([]);
    const [loading, setLoading] = useState(false);
    /** Escalator options + first pricing call — the modal has nothing to show until both land. */
    const [initializing, setInitializing] = useState(false);
    const loadGenRef = useRef(0);
    const initGenRef = useRef(0);

    const showBatteryTab = fetchBatteryPricing || batteryRows.length > 0;

    const loadPricing = useCallback(
        async (escalatorRate: string) => {
            if (opportunityId == null || String(opportunityId).trim() === '' || !escalatorRate.trim()) {
                setSolarRows([]);
                setBatteryRows([]);
                setLoading(false);
                return;
            }

            const gen = ++loadGenRef.current;
            setLoading(true);
            try {
                const { solarRows: solar, batteryRows: battery } = await fetchEnfinEligiblePricingRows({
                    opportunityId,
                    escalatorRate,
                    fetchBatteryPricing,
                });
                if (gen !== loadGenRef.current) return;
                setSolarRows(solar);
                setBatteryRows(battery);
            } catch {
                if (gen !== loadGenRef.current) return;
                setSolarRows([]);
                setBatteryRows([]);
            } finally {
                if (gen === loadGenRef.current) setLoading(false);
            }
        },
        [fetchBatteryPricing, opportunityId],
    );

    const initModal = useCallback(async () => {
        if (!priceQuote) return;
        const financeMethod = getEnfinSolarFinanceMethod(opportunityDetails);
        if (!financeMethod) return;

        const gen = ++loadGenRef.current;
        const initGen = ++initGenRef.current;
        setEscalatorOptions([]);
        setSelectedEscalator(undefined);
        setSolarRows([]);
        setBatteryRows([]);
        setInitializing(true);

        try {
            const options = await fetchEnfinEscalatorOptions({
                priceQuote,
                financeMethod,
                svaC4: getEnfinSvaC4(opportunityDetails, productType),
            });
            if (gen !== loadGenRef.current) return;

            setEscalatorOptions(options);
            const defaultEscalator = resolveDefaultEnfinEscalator(priceQuote, options);
            setSelectedEscalator(defaultEscalator || undefined);

            if (defaultEscalator) {
                await loadPricing(defaultEscalator);
            }
        } catch {
            if (gen !== loadGenRef.current) return;
            setEscalatorOptions([]);
            setSelectedEscalator(undefined);
            setSolarRows([]);
            setBatteryRows([]);
        } finally {
            if (initGen === initGenRef.current) setInitializing(false);
        }
    }, [loadPricing, opportunityDetails, priceQuote, productType]);

    useEffect(() => {
        if (!open) {
            loadGenRef.current += 1;
            initGenRef.current += 1;
            setLoading(false);
            setInitializing(false);
            return;
        }
        void initModal();
    }, [open, initModal]);

    const handleEscalatorChange = useCallback(
        (value: string) => {
            setSelectedEscalator(value);
            void loadPricing(value);
        },
        [loadPricing],
    );

    const tablePagination = useMemo(
        () => ({
            pageSize: 10,
            showSizeChanger: false,
            showTotal: (total: number, range: [number, number]) =>
                total > 0 ? `${range[0]}–${range[1]} of ${total}` : '',
            className: 'obd-lr-eligible-products-pagination',
        }),
        [],
    );

    const tabItems = useMemo(() => {
        const items = [
            {
                key: 'solar',
                label: 'Solar',
                children: (
                    <div className="obd-lr-eligible-products-table">
                        <AntTable<Record<string, unknown>>
                            size="middle"
                            loading={loading}
                            columns={ENFIN_ELIGIBLE_COLUMNS}
                            dataSource={solarRows}
                            rowKey={(_, i) => `enfin-solar-${i}`}
                            pagination={tablePagination}
                            // scroll={{  y: 450 }}
                            locale={{ emptyText: 'No record found' }}
                        />   
                    </div>
                ),
            },
        ];

        if (showBatteryTab) {
            items.push({
                key: 'solar-battery',
                label: 'Solar, Battery',
                children: (
                    <div className="obd-lr-eligible-products-table">
                        <AntTable<Record<string, unknown>>
                            size="middle"
                            loading={loading}
                            columns={ENFIN_ELIGIBLE_COLUMNS}
                            dataSource={batteryRows}
                            rowKey={(_, i) => `enfin-battery-${i}`}
                            pagination={tablePagination}
                            // scroll={{ y: 450 }}
                            locale={{ emptyText: 'No record found' }}
                        />
                    </div>
                ),
            });
        }

        return items;
    }, [batteryRows, fetchBatteryPricing, loading, showBatteryTab, solarRows, tablePagination]);

    return (
        <DashboardModal
            title="Eligible Products"
            open={open}
            onClose={onClose}
            footer={null}
            width="min(700px, 98vw)"
            theme={theme}
            maskClosable={false}
            destroyOnClose
            className="obd-dashboard-modal--lr-eligible-products"
            rootClassName={`obd-theme-${theme}`}
            zIndex={zIndex}
            loading={initializing}
            loadingMessage="Loading eligible products…"
        >
            <div className="obd-enfin-eligible-products__toolbar">
                <label className="obd-utility__field-label" htmlFor="enfin-eligible-escalator">
                    Escalator
                </label>
                <OBDSelect
                    id="enfin-eligible-escalator"
                    className="obd-enfin-eligible-products__escalator-select"
                    placeholder="Select"
                    disabled={escalatorOptions.length === 0}
                    value={selectedEscalator}
                    onChange={handleEscalatorChange}
                    options={escalatorOptions}
                />
            </div>
            <Tabs items={tabItems} />
        </DashboardModal>
    );
};

export default EnfinEligibleProductModal;
